import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { SEO } from '@/components/SEO';
import { RecordingCard } from '@/components/discovery/RecordingCard';
import { LiveStreamCard } from '@/components/discovery/LiveStreamCard';
import { EventCard } from '@/components/discovery/EventCard';
import { WalletCard } from '@/components/fan/WalletCard';
import { BadgesCard } from '@/components/fan/BadgesCard';
import { LeaderboardCard } from '@/components/fan/LeaderboardCard';
import { ScheduledStreamsCard } from '@/components/fan/ScheduledStreamsCard';
import { ReferralBanner } from '@/components/referral/ReferralBanner';
import { ClipsGallery } from '@/components/clips/ClipsGallery';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Radio, Music, Mic, Gamepad2, MessageSquare, Trophy } from 'lucide-react';
import { useStreamNotifications } from '@/hooks/useStreamNotifications';
import { toast } from 'sonner';

interface Recording {
  id: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string;
  view_count: number;
  duration: number;
  created_at: string;
  creator_id: string;
  profiles: {
    username: string;
    display_name: string;
    avatar_url: string;
    is_verified: boolean;
  };
  likes_count: number;
  comments_count: number;
  user_liked: boolean;
}

interface LiveStream {
  id: string;
  title: string;
  thumbnail_url: string | null;
  viewer_count: number;
  category: string | null;
  creator: {
    display_name: string;
    avatar_url: string | null;
    username: string;
    is_verified: boolean;
  };
}

interface Event {
  id: string;
  title: string;
  description: string | null;
  event_type: string;
  scheduled_at: string;
  duration_minutes: number | null;
  location: string | null;
  thumbnail_url: string | null;
  max_participants: number | null;
  creator_id: string;
  profiles?: {
    display_name: string | null;
    username: string;
    avatar_url: string | null;
    is_verified: boolean | null;
  };
  participant_count?: number;
  user_rsvp?: string | null;
}

export default function Discovery() {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [liveStreams, setLiveStreams] = useState<LiveStream[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStreams, setLoadingStreams] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'trending'>('recent');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [userId, setUserId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const STREAMS_PER_PAGE = 12;

  useStreamNotifications(userId);

  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setUserId(user.id);
    };
    getUserId();
    loadRecordings();
    loadLiveStreams();
    loadEvents();
    
    // Subscribe to realtime updates for live streams
    const channel = supabase
      .channel('streams-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'streams',
          filter: 'status=eq.live'
        },
        () => {
          loadLiveStreams();
        }
      )
      .subscribe();

    // Subscribe to realtime updates for events
    const eventsChannel = supabase
      .channel('events-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events'
        },
        () => {
          loadEvents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(eventsChannel);
    };
  }, [sortBy]);

  const loadLiveStreams = async (pageNum: number = 1, append: boolean = false) => {
    try {
      setLoadingStreams(true);
      const from = (pageNum - 1) * STREAMS_PER_PAGE;
      const to = from + STREAMS_PER_PAGE - 1;
      
      // Fetch live streams with creator profiles
      const { data: streamsData, error, count } = await supabase
        .from('streams')
        .select(`
          id,
          title,
          thumbnail_url,
          viewer_count,
          category,
          creator_id,
          profiles!streams_creator_id_fkey(display_name, avatar_url, username, is_verified)
        `, { count: 'exact' })
        .eq('status', 'live')
        .order('viewer_count', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error loading live streams:', error);
        // Don't show error toast, just log it
        setLiveStreams([]);
        setLoadingStreams(false);
        return;
      }

      // Transform data to match interface
      const transformedStreams = (streamsData || []).map(stream => {
        const profiles = Array.isArray(stream.profiles) ? stream.profiles[0] : stream.profiles;
        return {
          id: stream.id,
          title: stream.title,
          thumbnail_url: stream.thumbnail_url,
          viewer_count: stream.viewer_count || 0,
          category: stream.category,
          creator: {
            display_name: profiles?.display_name || 'Unknown',
            avatar_url: profiles?.avatar_url || null,
            username: profiles?.username || 'user',
            is_verified: profiles?.is_verified || false,
          }
        };
      });

      if (append) {
        setLiveStreams(prev => [...prev, ...transformedStreams]);
      } else {
        setLiveStreams(transformedStreams);
      }
      
      setHasMore((count || 0) > pageNum * STREAMS_PER_PAGE);
    } catch (error) {
      console.error('Error loading live streams:', error);
      setLiveStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  const loadMoreStreams = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadLiveStreams(nextPage, true);
  };

  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      
      // Fetch upcoming events
      const { data: eventsData, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_published', true)
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true })
        .limit(12);

      if (error) throw error;

      // Enrich with creator profiles, participant counts and user RSVP status
      const enrichedEvents = await Promise.all(
        (eventsData || []).map(async (event) => {
          // Get creator profile
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('display_name, username, avatar_url, is_verified')
            .eq('id', event.creator_id)
            .single();

          // Get participant count
          const { count } = await supabase
            .from('event_participants')
            .select('*', { count: 'exact', head: true })
            .eq('event_id', event.id);

          // Check if current user has RSVP'd
          let userRsvp = null;
          if (userId) {
            const { data: rsvpData } = await supabase
              .from('event_participants')
              .select('status')
              .eq('event_id', event.id)
              .eq('user_id', userId)
              .maybeSingle();
            userRsvp = rsvpData?.status || null;
          }

          return {
            ...event,
            profiles: creatorProfile,
            participant_count: count || 0,
            user_rsvp: userRsvp,
          };
        })
      );

      setEvents(enrichedEvents as Event[]);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All', icon: Radio },
    { value: 'music', label: 'Music', icon: Music },
    { value: 'comedy', label: 'Comedy', icon: Mic },
    { value: 'gaming', label: 'Gaming', icon: Gamepad2 },
    { value: 'sports', label: 'Sports', icon: Trophy },
    { value: 'talk', label: 'Talk', icon: MessageSquare },
  ];

  const filteredStreams = selectedCategory === 'all' 
    ? liveStreams 
    : liveStreams.filter(s => s.category?.toLowerCase() === selectedCategory);

  const loadRecordings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      // Build query based on sort
      let query = supabase
        .from('recordings')
        .select(`
          *,
          profiles!recordings_creator_id_fkey(username, display_name, avatar_url, is_verified)
        `)
        .eq('is_published', true)
        .eq('status', 'completed');

      // Apply sorting
      if (sortBy === 'recent') {
        query = query.order('published_at', { ascending: false });
      } else if (sortBy === 'popular') {
        query = query.order('view_count', { ascending: false });
      }

      const { data: recordingsData, error } = await query.limit(50);

      if (error) throw error;

      // Fetch engagement data for each recording
      const enrichedRecordings = await Promise.all(
        (recordingsData || []).map(async (recording) => {
          // Get likes count
          const { count: likesCount } = await supabase
            .from('recording_likes')
            .select('*', { count: 'exact', head: true })
            .eq('recording_id', recording.id);

          // Get comments count
          const { count: commentsCount } = await supabase
            .from('recording_comments')
            .select('*', { count: 'exact', head: true })
            .eq('recording_id', recording.id);

          // Check if current user liked this
          let userLiked = false;
          if (user) {
            const { data: likeData } = await supabase
              .from('recording_likes')
              .select('id')
              .eq('recording_id', recording.id)
              .eq('user_id', user.id)
              .maybeSingle();
            userLiked = !!likeData;
          }

          return {
            ...recording,
            likes_count: likesCount || 0,
            comments_count: commentsCount || 0,
            user_liked: userLiked,
          };
        })
      );

      setRecordings(enrichedRecordings as Recording[]);
    } catch (error) {
      console.error('Error loading recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Discover Live Performances & Recordings"
        description="Watch live shows, explore recordings, vote for your favorite songs, tip creators, and climb the leaderboard on HiStage."
        keywords={[
          "live performances",
          "watch live shows",
          "stream recordings",
          "interactive streaming",
          "fan voting",
          "tip creators"
        ]}
      />
      <Navigation />

      <div className="container py-8 flex-1">
        <BackButton to="/" />
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* Main Content */}
          <div className="space-y-8">
            {/* Live Streams Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold mb-2">Live Performances</h1>
                  <p className="text-muted-foreground">
                    Join live shows, vote, tip, and engage with creators
                  </p>
                </div>
                <Badge variant="destructive" className="animate-pulse">
                  <Radio className="h-4 w-4 mr-1.5" />
                  {liveStreams.length} LIVE
                </Badge>
              </div>

              {/* Category Tabs */}
              <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
                <div className="relative mb-6">
                  <div className="overflow-x-auto scrollbar-hide pb-2">
                    <TabsList className="bg-white border inline-flex w-auto min-w-full md:w-auto">
                      {categories.map((cat) => (
                        <TabsTrigger 
                          key={cat.value} 
                          value={cat.value}
                          className="gap-2 min-h-[44px] md:min-h-0 px-4 md:px-3 touch-manipulation whitespace-nowrap"
                        >
                          <cat.icon className="h-4 w-4" />
                          {cat.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none md:hidden" />
                </div>

                <TabsContent value={selectedCategory}>
                  {loadingStreams ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="space-y-3">
                          <Skeleton className="aspect-video w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      ))}
                    </div>
                  ) : filteredStreams.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredStreams.map((stream) => (
                          <LiveStreamCard key={stream.id} stream={stream} />
                        ))}
                      </div>
                      {hasMore && !loadingStreams && (
                        <div className="flex justify-center mt-6">
                          <Button onClick={loadMoreStreams} variant="outline" size="lg">
                            Load More Streams
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Radio className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-20" />
                      <h3 className="text-lg font-semibold mb-2">No live streams</h3>
                      <p className="text-muted-foreground">
                        {selectedCategory === 'all' 
                          ? 'Check back later for live performances' 
                          : `No ${selectedCategory} streams are live right now`}
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Upcoming Events Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-1">Upcoming Events</h2>
                  <p className="text-sm text-muted-foreground">
                    Discover and RSVP to upcoming streams, meetups, and announcements
                  </p>
                </div>
              </div>

              {loadingEvents ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-3">
                      <Skeleton className="h-48 w-full rounded-lg" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <p className="text-muted-foreground">No upcoming events at the moment</p>
                  <p className="text-sm text-muted-foreground mt-1">Check back later for new events!</p>
                </div>
              )}
            </div>

            {/* Recordings Section */}
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">Recordings</h2>
                <p className="text-muted-foreground">
                  Watch past performances and highlights
                </p>
              </div>

              <Tabs value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                <TabsList className="mb-6">
                  <TabsTrigger value="recent">Recent</TabsTrigger>
                  <TabsTrigger value="popular">Popular</TabsTrigger>
                  <TabsTrigger value="trending">Trending</TabsTrigger>
                </TabsList>

                <TabsContent value={sortBy}>
                  {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="space-y-3">
                          <Skeleton className="aspect-video w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      ))}
                    </div>
                  ) : recordings.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {recordings.map((recording) => (
                        <RecordingCard
                          key={recording.id}
                          recording={recording}
                          onUpdate={loadRecordings}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">
                        No recordings available yet. Check back later!
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Clips Section */}
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">Trending Clips</h2>
                <p className="text-muted-foreground">
                  Discover the best moments from your favorite creators
                </p>
              </div>
              <ClipsGallery limit={6} />
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            <WalletCard />
            <ReferralBanner />
            <ScheduledStreamsCard />
            <BadgesCard />
            <LeaderboardCard />
          </div>
        </div>
      </div>
    </div>
  );
}
